import i18n from 'i18next';

import { endpoints } from '@/api/endpoints';

// The backend-owned label catalog returns flat dotted keys ({ "adminStatus.PENDING": "미가입", ... }).
// Unflatten to nested ({ adminStatus: { PENDING: "미가입" } }) so i18next resolves t('adminStatus.PENDING')
// with its default '.' key separator.
function unflatten(flat: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let node = out;
    for (let i = 0; i < parts.length - 1; i += 1) {
      node[parts[i]] ??= {};
      node = node[parts[i]] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]] = value;
  }
  return out;
}

const I18N_URL = `${import.meta.env.VITE_API_BASE_URL ?? ''}${endpoints.i18n}`;

async function fetchBundle(locale?: 'en'): Promise<Record<string, string>> {
  // The shipped path sends NO locale param (server default = ko); only the dev-only `?locale=en`
  // toggle adds one, so production traffic and its ETag cache keys are unchanged.
  const url = locale ? `${I18N_URL}?locale=${locale}` : I18N_URL;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`i18n label bundle fetch failed (${res.status})`);
  }
  return (await res.json()) as Record<string, string>;
}

/**
 * Fetch the backend-owned UI-label catalog and register it as the i18next `labels` namespace for the
 * resolved locale. ko is the only shipped locale; under the dev-only `?locale=en` toggle the English
 * catalog is registered as `en` and the ko catalog is ALSO loaded, so any key the en catalog lacks falls
 * back (`fallbackLng: ko`) to a real label instead of rendering the raw key. The browser revalidates via
 * ETag on each load (a 304 returns the cached copy, no re-download); the bundle lives only in memory.
 * Public endpoint — no auth token, so it works on pre-auth screens. Throws on failure so the app's
 * loading gate can show a retry-able fallback (text must not render against a missing catalog).
 */
export async function loadRemoteLabelBundle(): Promise<void> {
  if (i18n.resolvedLanguage === 'en') {
    const [en, ko] = await Promise.all([fetchBundle('en'), fetchBundle()]);
    // overwrite=true so a redeploy's new labels replace the in-memory copy.
    i18n.addResourceBundle('en', 'labels', unflatten(en), true, true);
    i18n.addResourceBundle('ko', 'labels', unflatten(ko), true, true);
    return;
  }
  const flat = await fetchBundle();
  // overwrite=true so a redeploy's new labels replace the in-memory copy.
  i18n.addResourceBundle('ko', 'labels', unflatten(flat), true, true);
}
